import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@1trading/react';
import { Download } from 'lucide-react';
import { usePresignedDownload } from '@/hooks/use-presigned-download';
import type { FeeRun, FeeRunReports as FeeRunReportKeys } from '@/services/api/fee-runs';

type ReportKey = keyof FeeRunReportKeys;

const REPORTS: ReadonlyArray<{ key: ReportKey; label: string; description: string }> = [
  { key: 'usersCsvKey', label: 'Account holders', description: 'One row per account holder: entity, schedule, balances and final fee' },
  { key: 'chargesCsvKey', label: 'Charges', description: 'One row per balance that would be debited' },
  { key: 'exclusionsCsvKey', label: 'Exclusions', description: 'Everyone with a balance who is not charged, and why' },
  { key: 'planKey', label: 'Plan summary', description: 'Parameters, snapshot, totals and safety checks (JSON)' },
  { key: 'treasuryCsvKey', label: 'Treasury report', description: 'What was collected, by fee type, source and coin' },
  { key: 'collectedCsvKey', label: 'Collected charges', description: 'Every charge and its outcome after collection' },
];

/** Download buttons for the run's files in the BOP bucket, through presigned URLs. */
export const FeeRunReports = ({ run }: { run: FeeRun }) => {
  const { download, isDownloading } = usePresignedDownload();
  const available = REPORTS.flatMap((report) => {
    const fileKey = run.reports?.[report.key];
    return fileKey ? [{ ...report, fileKey }] : [];
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reports</CardTitle>
      </CardHeader>
      <CardContent>
        {available.length === 0 ? (
          <p className="text-sm text-muted-foreground">Reports appear once the calculation completes.</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {available.map((report) => (
              <li className="flex items-center justify-between gap-3 py-2" key={report.key}>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{report.label}</span>
                  <span className="text-xs text-muted-foreground">{report.description}</span>
                </div>
                <Button
                  aria-label={`Download ${report.label}`}
                  className="gap-2"
                  disabled={isDownloading(report.fileKey)}
                  onClick={() => void download(report.fileKey)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  {isDownloading(report.fileKey) ? <Spinner className="size-4" /> : <Download className="size-4" />}
                  Download
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
FeeRunReports.displayName = 'FeeRunReports';
